/* Native Shopify page content remains the authority. No publication or content writes. */
export function mountInformationNavigation(win = window, doc = document) {
  const selector = '.ub-native-information';
  if (!doc.querySelector(selector)) return;
  const mark = value => { if (doc.documentElement) doc.documentElement.dataset.ubInformationNavigation = value; };
  mark('ready');
  let controller, sequence = 0;
  const previewParameters = ['preview_theme_id', '_ab', '_fd', '_sc', 'key'];
  const contextualURL = href => {
    const target = new URL(href, win.location.href);
    const current = new URL(win.location.href);
    for (const key of previewParameters) {
      if (!target.searchParams.has(key) && current.searchParams.has(key))
        target.searchParams.set(key, current.searchParams.get(key));
    }
    return target;
  };
  const savePosition = () => win.history.replaceState({ ...win.history.state,
    ubInformation: true, scrollX: win.scrollX, scrollY: win.scrollY }, '', win.location.href);
  savePosition();
  async function navigate(href, traversal = false, position = null) {
    const url = contextualURL(href);
    controller?.abort();
    controller = new AbortController();
    const request = controller;
    const ticket = ++sequence;
    const current = doc.querySelector(selector);
    if (!current || url.origin !== win.location.origin) { win.location.assign(url.href); return; }
    if (!traversal) savePosition();
    mark('loading');
    current.setAttribute('aria-busy', 'true');
    const timeout = win.setTimeout(() => request.abort(), 12000);
    try {
      const response = await win.fetch(url.href, { signal: request.signal,
        credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw Error('Native page unavailable');
      const finalURL = new URL(response.url || url.href);
      if (finalURL.origin !== url.origin || finalURL.pathname !== url.pathname) throw Error('Native page redirected');
      const parsed = new win.DOMParser().parseFromString(await response.text(), 'text/html');
      let next = parsed.querySelector(selector);
      // Shopify's actual privacy policy is native policy content, not a duplicate coded page.
      if (!next && url.pathname.includes('/policies/')) {
        const policy = parsed.querySelector('.shopify-policy__container');
        const body = policy?.querySelector('.shopify-policy__body');
        const heading = policy?.querySelector('h1');
        if (body && heading) {
          next = current.cloneNode(true);
          next.classList?.remove('is-contact-page', 'ub-reviews-page');
          next.querySelector('.info-content').replaceChildren(...[...body.childNodes].map(n => n.cloneNode(true)));
          next.querySelector('h1').textContent = heading.textContent;
          next.querySelector('.micro').textContent = 'Help / ' + heading.textContent;
        }
      }
      if (!next) throw Error('Native information layout missing');
      next.querySelectorAll('script').forEach(n => n.remove());
      next.querySelectorAll('.info-nav a').forEach(link => {
        const active = new URL(link.getAttribute('href'), url.href).pathname === url.pathname;
        link.classList.toggle('active', active);
        if (active) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
      });
      if (ticket !== sequence) return;
      const sourceArticle = next.querySelector('.info-content');
      const sourceHeading = next.querySelector('h1');
      const currentArticle = current.querySelector('.info-content');
      if (!sourceArticle || !sourceHeading || !currentArticle) throw Error('Native article missing');
      const article = doc.importNode(sourceArticle, true);
      article.removeAttribute('aria-busy');
      currentArticle.replaceWith(article);
      const heading = current.querySelector('h1');
      heading.replaceChildren(...[...sourceHeading.childNodes].map(n => doc.importNode(n, true)));
      const micro = current.querySelector('.micro');
      if (micro) micro.textContent = next.querySelector('.micro')?.textContent || 'Help / Privacy';
      current.className = next.className;
      current.querySelectorAll('.info-nav a').forEach(link => {
        const active = new URL(link.getAttribute('href'), url.href).pathname === url.pathname;
        link.classList.toggle('active', active);
        if (active) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
      });
      doc.title = parsed.title || doc.title;
      for (const selector of ['meta[name="description"]', 'link[rel="canonical"]']) {
        const source = parsed.querySelector(selector), existing = doc.querySelector(selector);
        if (source && existing) existing.replaceWith(doc.importNode(source, true));
      }
      if (!traversal) win.history.pushState({ ubInformation: true, scrollX: win.scrollX, scrollY: win.scrollY }, '', url.href);
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      if (url.hash) doc.getElementById(decodeURIComponent(url.hash.slice(1)))?.scrollIntoView({ behavior: 'instant' });
      else if (position) win.scrollTo({ left: position.scrollX || 0, top: position.scrollY || 0, behavior: 'instant' });
      else {
        const headerBottom = doc.querySelector('.site-header')?.getBoundingClientRect?.().bottom || 0;
        const top = current.querySelector('.micro')?.getBoundingClientRect?.().top;
        if (typeof top === 'number' && top < headerBottom + 24) win.scrollTo({ left: win.scrollX, top: Math.max(0, win.scrollY + top - headerBottom - 24), behavior: 'instant' });
      }
      mark('same-document');
      if (!win.matchMedia('(prefers-reduced-motion: reduce)').matches)
        article.animate?.([{ opacity: 0, transform: 'translateY(5px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 160, easing: 'ease-out' });
    } catch (error) {
      // Only the latest request may fall back. Superseded clicks cannot navigate away.
      if (ticket === sequence) { mark('fallback-' + (error.name === 'AbortError' ? 'timeout' : ({'Native page unavailable':'response','Native page redirected':'redirect','Native information layout missing':'layout','Native article missing':'article'}[error.message] || 'runtime'))); win.location.assign(url.href); }
    } finally {
      win.clearTimeout(timeout);
      if (ticket === sequence) doc.querySelector(selector)?.removeAttribute('aria-busy');
    }
  }
  const onClick = event => {
    const link = event.target.closest?.('.info-nav a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = contextualURL(link.href);
    if (url.origin !== win.location.origin || !(/\/pages\//.test(url.pathname) || /\/policies\//.test(url.pathname))) return;
    // Preserve ordinary browser behavior for an unsent, edited contact form.
    const form = doc.querySelector(selector)?.querySelector('form');
    if (form && [...form.elements].some(e => (e.type === 'checkbox' || e.type === 'radio') ? e.checked !== e.defaultChecked : e.value !== undefined && e.defaultValue !== undefined && e.value !== e.defaultValue)) return;
    if (url.pathname === win.location.pathname && url.search === win.location.search) {
      ++sequence; controller?.abort(); doc.querySelector(selector)?.removeAttribute('aria-busy');
      if (!url.hash) event.preventDefault();
      return;
    }
    event.preventDefault();
    void navigate(url.href);
  };
  const onPop = event => { void navigate(win.location.href, true, event.state); };
  doc.addEventListener('click', onClick);
  win.addEventListener('popstate', onPop);
  return { navigate, contextualURL, destroy() { ++sequence; controller?.abort(); doc.removeEventListener('click', onClick); win.removeEventListener('popstate', onPop); } };
}
if (typeof window !== 'undefined' && typeof document !== 'undefined') mountInformationNavigation();
